import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button.js';

// Pages are lazy-loaded, so opening one that has not been visited yet fetches a
// chunk over the network. A builder on a site with one bar can have that fetch
// fail mid-session — without this, the rejected import unwinds past Suspense and
// leaves a white screen with no way back. Give the builder a way out instead.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

function isChunkLoadError(error: Error): boolean {
  return /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    `${error.name} ${error.message}`
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page failed to load', error, info.componentStack);
  }

  // Only a reload is offered, not an in-place retry: React.lazy caches a rejected
  // import for the lifetime of the page, so re-rendering the same page would
  // re-throw without refetching. lazyPage() already retried the transient case.
  private reload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const chunkFailure = isChunkLoadError(error);

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          minHeight: '100vh',
          padding: 24,
          background: 'var(--light-bg)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 32 }}>{offline ? '📡' : '⚠️'}</div>
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text-primary)' }}>
          {offline ? 'No connection' : chunkFailure ? "This page didn't load" : 'Something went wrong'}
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', maxWidth: 320 }}>
          {offline
            ? 'Reconnect and try again — anything you captured offline is still queued.'
            : chunkFailure
              ? 'The connection dropped while loading. Your work is safe.'
              : error.message}
        </div>
        <Button onClick={this.reload}>Reload</Button>
      </div>
    );
  }
}
